import React from 'react';


export const LoadingSpinner = ({ size = 'md', color = 'primary' }) => {
  const sizeClasses = {
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem'
  };

  const colorClasses = {
    primary: 'var(--color-primary-500)',
    secondary: 'var(--color-secondary-500)',
    neutral: 'var(--color-neutral-500)'
  };

  return (
    <div
      style={{
        width: sizeClasses[size],
        height: sizeClasses[size],
        border: `2px solid var(--color-neutral-200)`,
        borderTop: `2px solid ${colorClasses[color]}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
  );
};


export const LoadingSkeleton = ({ lines = 3, height = '1rem' }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          style={{
            height,
            backgroundColor: 'var(--color-neutral-200)',
            borderRadius: 'var(--radius-md)',
            animation: 'pulse 2s ease-in-out infinite',
            animationDelay: `${index * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
};


export const LoadingState = ({ 
  message = 'Loading...', 
  description = null,
  size = 'md',
  showSpinner = true 
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-8)',
        textAlign: 'center',
        gap: 'var(--space-4)',
      }}
    >
      {showSpinner && <LoadingSpinner size={size} />}
      <div>
        <h3
          style={{
            margin: '0',
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--color-neutral-700)',
            marginBottom: description ? 'var(--space-2)' : '0',
          }}
        >
          {message}
        </h3>
        {description && (
          <p
            style={{
              margin: '0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-neutral-500)',
              lineHeight: 'var(--line-height-relaxed)',
            }}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
};


export const EmptyState = ({ 
  icon = '📄', 
  title = 'No data available', 
  description = null,
  action = null,
  style = {}
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-8)',
        textAlign: 'center',
        backgroundColor: 'var(--color-neutral-50)',
        borderRadius: 'var(--radius-lg)',
        border: `2px dashed var(--color-neutral-300)`,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-4xl)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {icon}
      </div>
      <h3
        style={{
          margin: '0',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-neutral-900)',
          marginBottom: description ? 'var(--space-2)' : '0',
        }}
      >
        {title}
      </h3>
      {description && (
        <p
          style={{
            margin: '0',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-neutral-600)',
            lineHeight: 'var(--line-height-relaxed)',
            marginBottom: action ? 'var(--space-4)' : '0',
          }}
        >
          {description}
        </p>
      )}
      {action && action}
    </div>
  );
};


export const ErrorState = ({ 
  title = 'Something went wrong', 
  message = null,
  onRetry = null,
  style = {}
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-8)',
        textAlign: 'center',
        backgroundColor: 'var(--color-error-50)',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid var(--color-error-200)`,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-4xl)',
          marginBottom: 'var(--space-4)',
        }}
      >
        ⚠️
      </div>
      <h3
        style={{
          margin: '0',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-error-700)',
          marginBottom: message ? 'var(--space-2)' : '0',
        }}
      >
        {title}
      </h3>
      {message && (
        <p
          style={{
            margin: '0',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-error-600)',
            lineHeight: 'var(--line-height-relaxed)',
            marginBottom: onRetry ? 'var(--space-4)' : '0',
          }}
        >
          {message}
        </p>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="btn-modern btn-outline"
          style={{
            borderColor: 'var(--color-error-300)',
            color: 'var(--color-error-600)',
          }}
        >
          Try Again
        </button>
      )}
    </div>
  );
};


export const ProgressBar = ({ 
  value = 0, 
  max = 100, 
  label = null,
  color = 'primary',
  size = 'md'
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  
  const heights = {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem'
  };

  const colors = {
    primary: 'var(--color-primary-500)',
    secondary: 'var(--color-secondary-500)',
    success: 'var(--color-success-500)',
    warning: 'var(--color-warning-500)',
    error: 'var(--color-error-500)'
  };

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-2)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-neutral-700)',
          }}
        >
          <span>{label}</span>
          <span>{percentage.toFixed(0)}%</span>
        </div>
      )}
      <div
        style={{
          width: '100%',
          height: heights[size],
          backgroundColor: 'var(--color-neutral-200)',
          borderRadius: 'var(--radius-full)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: colors[color],
            borderRadius: 'var(--radius-full)',
            transition: 'width var(--transition-base)',
          }}
        />
      </div>
    </div>
  );
};


const animations = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
`;


if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = animations;
  document.head.appendChild(style);
}
